/* eslint-disable sonarjs/no-identical-functions */
import productBaseMixin from '../../components/product/base-mixin';
import browseMixin from './browse-mixin';
import throttle from 'lodash-es/throttle';
import debounce from 'lodash-es/debounce';
import { randomSecond, sleep } from '@/assets/utils/utils';
import { BEFORE_DESTROY } from '@/assets/utils/compat';
import { isEmpty } from 'lodash-es';

const throttleTime = 300;

export default {
  mixins: [productBaseMixin, browseMixin],

  props: {
    /** 是否显示总数 */
    totalVisible: {
      type: Boolean,
      default: true,
    },
    /** 描述详情文本 */
    descLabel: {
      type: String,
      default: '',
    },
    /** 是否显示订单中心入口 */
    orderCenterVisible: {
      type: Boolean,
      default: false,
    },
    /** 后台预览模式 */
    localProductList: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // 是否正在请求中
      isLoading: false,
      // 第一次请求是否完毕，完毕之后才能显示 blank 页
      isFirstLoadDone: false,
      // 是否获取完所有的商品
      isNoMore: false,
      // 商品列表
      products: [],
      // 商品总数
      total: 0,
      // 置顶商品
      toppingProduct: {},
      // 列表宽度
      contentWidth: 270,
      currentTagId: '',
      // 搜索关键词
      keyword: '',
      // 当前是否处于关键词搜索
      isKeywordSearch: false,

      pageNumber: 1,
      pageSize: 10,
    };
  },

  mounted() {
    this.init();
    window.addEventListener('resize', this.getContentWidth);
  },

  [BEFORE_DESTROY]() {
    this.unlistenSdkEvent();
    window.removeEventListener('resize', this.getContentWidth);
  },

  watch: {
    productSdk: {
      immediate: true,
      handler() {
        this.listenSdkEvent();
      },
    },

    total: {
      immediate: true,
      handler() {
        this.$emit('total-change', this.total);
      },
    },

    currentTagId() {
      this.getProductList(true);
    },

    keyword: debounce(function() {
      this.products = [];
      this.getProductList(true);
    }, throttleTime, {}),
  },

  computed: {
    needSleepRefresh() {
      return this.currentTagId || this.keyword;
    },
    hasTopping() {
      return !isEmpty(this.toppingProduct);
    }
  },

  methods: {
    getContentWidth() {
      const $productContent = this.$refs.productContent;
      if ($productContent) {
        this.contentWidth = $productContent.clientWidth;
      }
    },

    init() {
      this.isLoading = false;
      this.isFirstLoadDone = false;
      this.isNoMore = false;
      this.keyword = '';
      this.isKeywordSearch = false;
      this.getProductList(true);
      this.getContentWidth();
    },

    handleScroll(e) {
      this.checkBrowseProduct();
      this.handleScrollDebounce(e);
    },

    handleScrollDebounce: throttle(function(e) {
      // 获取下一页数据
      const target = e.target;
      const scrollHeight = parseInt(target.scrollHeight);
      const scrollTop = parseInt(target.scrollTop);
      const clientHeight = parseInt(target.clientHeight);
      // 滚动时距离底部 150px 内触发下一页的数据获取
      if (scrollTop >= scrollHeight - clientHeight - 150) {
        this.getProductList();
      }
    }, throttleTime),

    async getProductList(isFirst = false) {
      if (isFirst) {
        this.pageNumber = 1;
        this.isNoMore = false;
        this.isLoading = false;
      }

      if (this.localProductList && this.localProductList.length > 0) {
        this.$nextTick(() => {
          this.getProductByLocalList(isFirst);
        });

        return;
      }

      if (this.currentTagId || this.keyword) {
        if (!isFirst) {
          this.pageNumber += 1;
        }
        await this.getProductListByTagId(isFirst);
      } else {
        await this.getProductListByNormal(isFirst);
      }

      this.isKeywordSearch = !!this.keyword;
    },

    getProductByLocalList(isFirst = false) {
      if (isFirst) {
        this.products = this.handleProducts(this.localProductList).map(item => {
          return {
            ...item,
            productBuyButtonPreviewMode: true
          };
        });
        this.isFirstLoadDone = true;
      } else {
        this.products = this.handleProducts([...this.products, ...this.localProductList.map(item => {
          return {
            ...item,
            productBuyButtonPreviewMode: true
          };
        })]);
      }

      this.total = this.products.length;
    },

    /**
     * 获取商品列表
     */
    async getProductListByNormal(isFirst = false) {
      if (this.isNoMore || this.isLoading || !this.productSdk) {
        return;
      }
      let showId = '';
      if (this.products.length && !isFirst) {
        showId = this.products[this.products.length - 1].showId;
      }

      this.isLoading = true;
      try {
        const res = await this.productSdk.getProductListByShowId({ showId, tagId: this.currentTagId });
        const { total = 0, content = [], toppingProduct = {} } = res;
        console.info('getProductListByNormal 商品列表', res);

        this.toppingProduct = toppingProduct;
        this.total = total;
        if (isFirst) {
          this.products = this.handleProducts(content);
          this.isFirstLoadDone = true;
          this.$nextTick(() => {
            this.$refs.productContent.scrollTop = 0;
          });
        } else {
          this.products = this.handleProducts([...this.products, ...content]);
        }
        if (content.length < 10) {
          this.isNoMore = true;
        }
      } catch (e) {}

      this.$nextTick(() => {
        this.isLoading = false;
        this.checkBrowseProduct();
      });
    },

    /**
     * 获取商品列表
     */
    async getProductListByTagId(isFirst = false) {
      if (this.isNoMore || this.isLoading || !this.productSdk) {
        return;
      }

      const keyword = this.keyword;
      this.isLoading = true;
      let tagId = this.currentTagId;

      if (keyword) {
        tagId = '';
      }

      try {
        const res = await this.productSdk.getProductListByTagId({
          tagId,
          keyword,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
        });
        const { totalItems = 0, contents = [], totalPages, pageNumber } = res;
        console.info('getProductListByTagId 商品列表', res, keyword);
        console.info(totalItems, contents);
        this.toppingProduct = {};
        this.total = totalItems;
        this.pageNumber = pageNumber;
        if (isFirst) {
          this.products = this.handleProducts(contents);
          this.$nextTick(() => {
            this.$refs.productContent.scrollTop = 0;
          });
        } else {
          this.products = this.handleProducts([...this.products, ...contents]);
        }

        this.isNoMore = pageNumber >= totalPages;
      } catch (e) {}

      this.$nextTick(() => {
        this.isLoading = false;
        this.checkBrowseProduct();
      });
    },

    /**
     * 处理列表去重
     * @param {Array} products 商品列表
     */
    handleProducts(products = []) {
      let newProducts = [...products];
      const pIds = {};
      newProducts = newProducts.filter(good => {
        // 去重
        if (pIds[good.productId]) {
          return false;
        }
        if (this.hasTopping && this.toppingProduct.productId === good.productId) {
          return false;
        }
        pIds[good.productId] = true;
        // 去除不在架的商品
        return Number(good.status) === 1;
      });
      return newProducts;
    },

    /** 监听 sdk 事件 */
    listenSdkEvent() {
      if (!this.productSdk) {
        return;
      }
      const { events } = this.productSdk;
      this.productSdk.on(events.PRODUCT_MESSAGE, this.handleProductMessage);
      this.productSdk.on(events.RefreshProductList, this.handleRefreshProductList);
    },

    unlistenSdkEvent() {
      if (!this.productSdk) {
        return;
      }
      const { events } = this.productSdk;
      this.productSdk.off(events.PRODUCT_MESSAGE, this.handleProductMessage);
      this.productSdk.off(events.RefreshProductList, this.handleRefreshProductList);
    },

    handleRefreshProductList() {
      this.isNoMore = false;
      this.sleepRefreshCurrentList();
    },

    /**
     * 处理商品库消息
     */
    handleProductMessage() {
      if (!this.productSdk) {
        return;
      }
      this.sleepRefreshCurrentList();
      // const ProductMessageStatus = this.productSdk.ProductMessageStatus;
      // const status = `${msg.status}`;
      //
      // switch (status) {
      //   case ProductMessageStatus.OnShelf:
      //     this.handleOnShelfProduct(msg.content);
      //     break;
      //   case ProductMessageStatus.OffShelf:
      //   case ProductMessageStatus.DeleteProduct:
      //     this.handleOffShelfAndDel(msg.content);
      //     break;
      //   case ProductMessageStatus.AddProduct:
      //     this.handleAddProduct(msg.content);
      //     break;
      //   case ProductMessageStatus.ChangeProduct:
      //     this.handleChangeProduct(msg.content);
      //     break;
      //   case ProductMessageStatus.MoveUp:
      //   case ProductMessageStatus.MoveDown:
      //     if (msg.content.length !== 2 && !this.products.length) {
      //       return;
      //     }
      //     this.handleMoveProduct(msg.content);
      //     break;
      //   case ProductMessageStatus.ChangeRank:
      //     this.handleChangeRank(msg.content);
      //     break;
      //   default:
      //     break;
      // }

      // 经过上面的处理后重新处理一次当前的商品数组
      // this.products = this.handleProducts(this.products);
      this.checkBrowseProduct();
    },
    // 暂时注释因为此处排序行为已交由服务端处理
    // 操作1 - 上架商品
    // handleOnShelfProduct(data) {
    //   // if (this.needSleepRefresh) {
    //   this.sleepRefreshCurrentList();
    //   // return;
    //   // }
    //   // this.total++;
    //   // const len = this.products.length;
    //   //
    //   // if (len === 0) {
    //   //   this.products.push(data);
    //   //   return;
    //   // }
    //   // // 上架的商品 showId 小于最后一个
    //   // let pushInList = true;
    //   // if (
    //   //   data.showId < this.products[len - 1].showId
    //   // ) {
    //   //   this.isNoMore = false;
    //   //   pushInList = false;
    //   // }
    //   //
    //   // let products = this.products.map(item => {
    //   //   return {
    //   //     ...item,
    //   //     showId: item.showId >= data.showId ? item.showId + 1 : item.showId,
    //   //   };
    //   // });
    //   //
    //   // if (pushInList) {
    //   //   products = [data, ...products];
    //   // }
    //   // this.products = this.handleProducts(products);
    // },

    // 操作2 - 下架商品
    // 操作3 - 删除商品
    // handleOffShelfAndDel(data) {
    //   // if (this.needSleepRefresh) {
    //   this.sleepRefreshCurrentList();
    //   // return;
    //   // }
    //   // this.total--;
    //   // const inProducts = this.isInProducts(data);
    //   // if (inProducts) {
    //   //   const gindex = this.products.findIndex(item => item.productId === data.productId);
    //   //   const products = this.products.map((item, index) => {
    //   //     return {
    //   //       ...item,
    //   //       showId: index < gindex ? item.showId - 1 : item.showId,
    //   //     };
    //   //   });
    //   //   this.products = products.filter(
    //   //     item => item.productId !== data.productId
    //   //   );
    //   // } else {
    //   //   this.products = this.products.map(item => {
    //   //     return {
    //   //       ...item,
    //   //       showId: item.showId - 1,
    //   //     };
    //   //   });
    //   // }
    //   //
    //   // this.checkScrollList();
    // },

    // 操作4 - 添加商品
    // handleAddProduct(data) {
    //   // if (this.needSleepRefresh) {
    //   this.sleepRefreshCurrentList();
    //   // return;
    //   // }
    //   // if (data.status !== 1) return;
    //   // this.total++;
    //   // this.products = [data, ...this.products];
    // },

    // 操作5 - 修改商品
    // handleChangeProduct(data) {
    //   // if (this.needSleepRefresh) {
    //   this.sleepRefreshCurrentList();
    //   // return;
    //   // }
    //   // const index = this.products.findIndex(
    //   //   item => item.productId === data.productId
    //   // );
    //   // 在列表里，直接更新
    //   // if (index !== -1) {
    //   //   const products = [...this.products];
    //   //   products[index] = data;
    //   //   this.products = products;
    //   // }
    // },

    // 操作6 - 上移商品
    // 操作7 - 下移商品
    // handleMoveProduct(data) {
    //   // if (this.needSleepRefresh) {
    //   this.sleepRefreshCurrentList();
    //   // return;
    //   // }
    //   // let index1 = -1;
    //   // let index2 = -1;
    //   // for (let i = 0; i < this.products.length; i++) {
    //   //   const item = this.products[i];
    //   //   if (item.productId === data[0].productId) {
    //   //     index1 = i;
    //   //   }
    //   //   if (item.productId === data[1].productId) {
    //   //     index2 = i;
    //   //   }
    //   //   if (index1 !== -1 && index2 !== -1) {
    //   //     break;
    //   //   }
    //   // }
    //   // const isInProducts = index1 !== -1 || index2 !== -1;
    //   // if (!isInProducts) {
    //   //   return;
    //   // }
    //   // const newList = this.handleProducts([...data, ...this.products]);
    //   // this.products = newList;
    // },

    // 操作15 - 修改排序
    // async handleChangeRank(data) {
    //   if (this.needSleepRefresh) {
    //     this.sleepRefreshCurrentList();
    //     return;
    //   }
    //   const isInProduct = this.products.some(item => {
    //     // 改动的商品在当前列表
    //     if (item.productId === data.productId) {
    //       return true;
    //     }
    //
    //     // 改动的商品之后就在当前列表
    //     if (item.showId <= data.showId) {
    //       return true;
    //     }
    //     return false;
    //   });
    //
    //   if (isInProduct) {
    //     await this.sleepRefreshCurrentList();
    //   }
    // },

    // isInProducts(good) {
    //   return this.products.some(item => item.productId === good.productId);
    // },

    checkScrollList() {
      // 滚动条消失时需要获取下一批数据，避免无法滚动
      this.$nextTick(() => {
        const productContent = this.$refs.productContent;
        if (!productContent) {
          return;
        }
        const canScroll = productContent.scrollHeight > productContent.clientHeight;
        if (!canScroll) {
          this.getProductList();
        }
      });
    },

    /**
     * 0.5-5 秒内随机延迟后刷新当前整个列表
     */
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async sleepRefreshCurrentList() {
      const sleepSeond = Number(randomSecond(500, 5000).toFixed(0));
      await sleep(sleepSeond);
      this.isLoading = true;

      let isNoMore = false;
      if (this.currentTagId || this.keyword) {
        try {
          let tagId = this.currentTagId;
          if (this.keyword) {
            tagId = '';
          }
          const pageSize = this.products.length < 10 ? 10 : this.products.length;
          const res = await this.productSdk.getProductListByTagId({
            tagId,
            keyword: this.keyword,
            pageNumber: 1,
            pageSize,
          });
          const { totalItems = 0, contents = [] } = res;
          console.info('sleepRefreshCurrentList 商品列表', res);
          isNoMore = contents.length < pageSize;
          this.total = totalItems;
          this.products = contents;
          this.toppingProduct = {};
        } catch (e) {}
      } else {
        try {
          const count = this.products.length < 10 ? 10 : this.products.length;
          const res = await this.productSdk.getProductListByShowId({
            count,
          });
          console.info('sleepRefreshCurrentList 商品列表', res);
          const { content, total, toppingProduct } = res;
          isNoMore = content.length < count;
          this.total = total;
          if (isEmpty(toppingProduct)) {
            this.products = content;
          } else {
            this.products = content.filter(item => item.productId !== toppingProduct.productId);
          }
          this.toppingProduct = toppingProduct;
        } catch (e) {}
      }

      this.isKeywordSearch = !!this.keyword;

      this.$nextTick(() => {
        this.isLoading = false;
        this.checkBrowseProduct();

        this.isNoMore = isNoMore;
        this.checkScrollList();
      });
    },

    clickJobDetail($event) {
      this.$emit('job-detail', $event);
      this.clickBuyTrackLog($event);
    },

    clickProduct(good) {
      if (this.productSdk) {
        this.clickBuyTrackLog(good);
      }

      this.$emit('click-product', good);
    },

    clickExplain(good) {
      this.$emit('click-explain', good);
    },

    /** 点击按钮进入订单中心 */
    enterOrderCenter() {
      this.$emit('enter-order-center');
    },

    handleSearchFocus() {
      this.$emit('product-search-focus');
    },

    handleSearchBlur() {
      this.$emit('product-search-blur');
    }
  },
};
